/**
 * Step 3 — Local database setup.
 *
 * Asks for confirmation, then creates/migrates the SQLite database and validates
 * the schema (all in the main process). Cancel is offered ONLY before creation
 * starts; once it begins, the operation is idempotent and safe to re-run.
 */

import React, { useState } from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import sx from '../setupStyles';
import { setupApi } from '../desktopApi';

export default function Database({ goBack, goNext, patchCtx }) {
  const [phase, setPhase] = useState('confirm'); // confirm | running | done | error
  const [error, setError] = useState('');

  const start = async () => {
    setPhase('running');
    setError('');
    try {
      const res = await setupApi.initDatabase();
      if (res && res.ok) {
        patchCtx({ dbReady: true });
        setPhase('done');
      } else {
        setError(`Schema validation failed (missing: ${(res && res.missing ? res.missing : []).join(', ') || 'unknown'})`);
        setPhase('error');
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Database setup failed');
      setPhase('error');
    }
  };

  const cancel = () => {
    if (typeof window !== 'undefined') window.close();
  };

  return (
    <>
      <Box sx={sx.body}>
        <Box sx={sx.centerCol}>
          <Typography variant="h6" fontWeight={600}>
            Configure the local database
          </Typography>

          {phase === 'confirm' && (
            <Typography color="text.secondary">
              The application will now configure the local database required for offline
              functionality. This creates a small database on this computer.
            </Typography>
          )}

          {phase === 'running' && (
            <Stack spacing={1}>
              <Typography color="text.secondary">Initializing database…</Typography>
              <LinearProgress />
            </Stack>
          )}

          {phase === 'done' && (
            <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success">
              Local database configured successfully.
            </Alert>
          )}

          {phase === 'error' && <Alert severity="error">{error}</Alert>}
        </Box>
      </Box>
      <Box sx={sx.footer}>
        {phase === 'confirm' && (
          <>
            <Button color="inherit" onClick={goBack}>
              Back
            </Button>
            <Stack direction="row" spacing={1}>
              <Button color="error" onClick={cancel}>
                Cancel
              </Button>
              <Button variant="contained" onClick={start}>
                Start Setup
              </Button>
            </Stack>
          </>
        )}

        {phase === 'running' && (
          <>
            <span />
            <Button variant="contained" disabled>
              Working…
            </Button>
          </>
        )}

        {phase === 'error' && (
          <>
            <Button color="inherit" onClick={goBack}>
              Back
            </Button>
            <Button variant="contained" onClick={start}>
              Retry
            </Button>
          </>
        )}

        {phase === 'done' && (
          <>
            <span />
            <Button variant="contained" onClick={goNext}>
              Continue
            </Button>
          </>
        )}
      </Box>
    </>
  );
}
